import { useEffect, useState, type ReactNode } from 'react';
import type { ScreenEvent, ScreenEventKind } from '../game/useGameSession';
import { CardIcon } from './Icon';
import { useT } from '../i18n';

const VISUAL: Record<ScreenEventKind, { color: string; icon: ReactNode }> = {
  shield: { color: 'var(--cyan)', icon: <CardIcon kind="cipherShield" size={72} /> },
  firewall: { color: 'var(--cyan)', icon: <CardIcon kind="firewall" size={72} /> },
  free: { color: 'var(--lime)', icon: <span style={{ fontSize: 68 }}>🔓</span> },
  reboot: { color: 'var(--violet)', icon: <span style={{ fontSize: 68 }}>↺</span> },
  locked: { color: 'var(--amber)', icon: <CardIcon kind="downtime" size={72} /> },
  bounty: { color: 'var(--lime)', icon: <span style={{ fontSize: 68 }}>💰</span> },
  penalty: { color: 'var(--magenta)', icon: <span style={{ fontSize: 68 }}>🔥</span> },
};

const DURATION = 1900;

/**
 * Full-screen dramatic beat with a dimmed backdrop for events that happen to
 * the local player (defense triggered, broke free, rebooted). Shows the latest
 * event, then auto-dismisses it by id.
 */
export function ScreenFlash({ events }: { events: ScreenEvent[] }) {
  const t = useT();
  const latest = events.length ? events[events.length - 1]! : null;
  const [dismissedId, setDismissedId] = useState<number | null>(null);

  useEffect(() => {
    if (!latest || latest.id === dismissedId) return;
    const timer = setTimeout(() => setDismissedId(latest.id), DURATION);
    return () => clearTimeout(timer);
  }, [latest, dismissedId]);

  if (!latest || latest.id === dismissedId) return null;
  const v = VISUAL[latest.kind];
  if (!v) return null;
  const text = t.flash[latest.kind];

  return (
    <div className="screenflash" key={latest.id} aria-hidden>
      <div className="sf-inner" style={{ color: v.color }}>
        <div className="sf-ring" />
        <div className="sf-glyph">{v.icon}</div>
        <div className="sf-title">{text.title}</div>
        <div className="sf-sub">{text.sub}</div>
      </div>
    </div>
  );
}
